import React from 'react';
import { fmtNum, TOOLTIP_W, TOOLTIP_H_EST } from './GtChartHelpers';

export interface GtTooltipRow {
    name: string;
    value: number;
    color: string;
}

interface GtChartTooltipProps {
    visible: boolean;
    cx: number;
    cy: number;
    catLabel: string;
    rows: GtTooltipRow[];
    isDark: boolean;
    containerW: number;
    height: number;
    tooltipHead: string;
    tooltipSub: string;
    formatTooltipValue?: (value: number, seriesName: string, catLabel: string) => string;
}

export function GtChartTooltip({
    visible,
    cx,
    cy,
    catLabel,
    rows,
    isDark,
    containerW,
    height,
    tooltipHead,
    tooltipSub,
    formatTooltipValue,
}: GtChartTooltipProps) {
    if (!visible || rows.length === 0) return null;

    // Smart placement: right of cursor, flip left near edge
    let tx = cx + 16;
    if (tx + TOOLTIP_W > containerW - 4) tx = cx - TOOLTIP_W - 12;
    if (tx < 4) tx = 4;
    let ty = cy - 16;
    if (ty < 4) ty = cy + 20;
    if (ty + TOOLTIP_H_EST > height + 20) ty = height - TOOLTIP_H_EST;

    return (
        <div
            style={{
                position: 'absolute',
                left: tx,
                top: ty,
                zIndex: 20,
                pointerEvents: 'none',
                background: isDark ? 'rgba(15,23,42,0.92)' : 'rgba(255,255,255,0.96)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.07)'}`,
                borderRadius: 12,
                overflow: 'hidden',
                boxShadow: isDark
                    ? '0 16px 40px rgba(0,0,0,0.65), 0 0 0 0.5px rgba(255,255,255,0.06)'
                    : '0 8px 32px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
                minWidth: 148,
                fontSize: 11.5,
                fontFamily: 'inherit',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
            }}
        >
            {/* Header */}
            <div
                style={{
                    padding: '8px 12px 6px',
                    borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
                }}
            >
                <span style={{
                    color: isDark ? '#cbd5e1' : '#475569',
                    fontWeight: 600,
                    fontSize: 10.5,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                }}>
                    {catLabel}
                </span>
            </div>
            {/* Series rows */}
            <div style={{ padding: '7px 12px 9px' }}>
                {rows.map((row, ri) => (
                    <div
                        key={`${row.name}-${ri}`}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginTop: ri > 0 ? 5 : 0,
                        }}
                    >
                        <span style={{
                            width: 8, height: 8, borderRadius: 3,
                            backgroundColor: row.color, flexShrink: 0,
                            boxShadow: `0 0 0 2px ${row.color}33`,
                        }} />
                        <span style={{
                            color: tooltipSub,
                            flex: 1, minWidth: 0,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            fontSize: 11,
                        }}>
                            {row.name}
                        </span>
                        <span style={{
                            color: tooltipHead,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                            letterSpacing: '-0.02em',
                            fontSize: 12,
                            marginLeft: 6,
                        }}>
                            {formatTooltipValue
                                ? formatTooltipValue(row.value, row.name, catLabel)
                                : fmtNum(row.value)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
